import React from 'react';
import { Select } from 'antd';
import { type, equals } from 'ramda';
import { stringify, parse } from 'qs';

const { Option } = Select;

// default transfer settings
const defaultTransfer = {
  key: 'code',
  code: 'code',
  name: 'name',
};
// utils
const generatorArray = (op) => (type(op) === 'Array' ? op : []);
const isFunction = (op) => type(op).includes('Function');

const AsyncSelect = (
  { source, customRequest, payload, transfer = defaultTransfer, optionRender, ...selectProps },
  ref,
) => {
  const [loading, setLoading] = React.useState(true);
  const [dataSource, setDataSource] = React.useState([]);

  // listener. when use source, use new source to trigger update.
  React.useEffect(() => {
    // update dataSource when componentDidUpdate.
    const gSource = generatorArray(source);
    setDataSource(gSource);
    setLoading(false);
  }, [source]);

  // listener. when use customRequest, use payload to trigger the request.
  // if payload is undefined, its just a initial request
  const flatPayload = stringify(payload);
  React.useEffect(() => {
    const payload = parse(flatPayload);
    let ignore = false; // when component un-mount, its unnecessary to update dataSource.
    async function fetchData(payload) {
      try {
        setLoading(true);
        const response = await customRequest(payload);
        const gSource = generatorArray(response);
        if (!ignore) setDataSource(gSource);
      } catch (error) {
        error.name = 'AsyncSelect';
        throw error;
      } finally {
        setLoading(false);
      }
    }
    // console.log('+++++ > ', payload);
    // check whether customRequest is a function or not. only a function can trigger the request.
    isFunction(customRequest) && fetchData(payload);
    return () => {
      ignore = true;
    };
  }, [flatPayload]); // eslint-disable-line

  // hack align, make it smoother
  const { dropdownMatchSelectWidth } = selectProps;
  const fixAlign = equals(dropdownMatchSelectWidth, false)
    ? { dropdownAlign: { points: ['tr', 'br'], overflow: false } }
    : {};

  const _optionRender = (data, index) => {
    // for custom Option render. optional.
    if (equals(type(optionRender), 'Function')) return optionRender(Option, data, index, transfer);
    // normal data structure. every item is an object. give a default destructuring model
    if (equals(type(data), 'Object')) {
      const { key, code, name } = transfer;
      return (
        <Option key={data[key]} value={data[code]}>
          {data[name]}
        </Option>
      );
    }
    // plain case, option maybe a plain array filled value-type includes 'String','Number',etc.
    return (
      <Option key={data} value={data}>
        {data}
      </Option>
    );
  };

  return (
    <Select ref={ref} loading={loading} {...fixAlign} {...selectProps}>
      {dataSource.map(_optionRender)}
    </Select>
  );
};

export default React.forwardRef(AsyncSelect);
